
import * as React from 'react';
import { debounce, deburr, isEmpty } from 'es-toolkit/compat';
import { noop } from 'es-toolkit';
import Fuse from 'fuse.js';

import clsx from 'clsx';

import { core, safeToNumber } from '@tock/coretypes';
import { useState } from 'react';
import BaseComponent from '../BaseComponent';
import BusinessSwitcherItem, {
  BusinessForSwitcher,
} from './BusinessSwitcherItem';
import LoadingComponent from '../LoadingComponent';
import TextInput from '../inputs/DeprecatedTextInput';

interface Props {
  activeBusiness?: core.Business;
  amountDisplayed?: number;
  businesses: BusinessForSwitcher[];
  pathname?: string;
  isEngOrHospitality: boolean;
  isLoading?: boolean;
  showOverlay?: boolean;
  scrollableModal?: boolean;
  onSelectBusiness(business: BusinessForSwitcher): void;
  placeholder?: string;
  onInputChange?: (inputValue: React.ChangeEvent<HTMLInputElement>) => void;
  selectedBusiness?: BusinessForSwitcher;
}

interface InternalProps extends Props {
  filter: string;
  handleFilterChange: (value: string) => void;
}

interface State {
  options: Option[];
  allOptions: Option[];
}

interface Option {
  business: BusinessForSwitcher;
  name: string;
  key: number;
}

const BusinessSwitcher: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const [filter, setFilter] = useState('');

  const handleFilterChange = (value: string) => {
    setFilter(value);
  };

  // Key is used here to trigger a re-mount of the BusinessSwitcherInternal as it depends on old componentDidMount code, but usages don't guarantee that businesses prop is populated. This is a good alternative to using componentDidUpdate(prone to infinite loops) or getDerivedStateFromProps
  return (
    <BusinessSwitcherInternal
      key={props.businesses.length}
      filter={filter}
      handleFilterChange={handleFilterChange}
      {...props}
    />
  );
};

class BusinessSwitcherInternal extends BaseComponent<InternalProps, State> {
  filterNode!: HTMLInputElement;
  static defaultProps: Partial<Props> = {
    amountDisplayed: 10,
    placeholder: 'Filter businesses by name',
  };

  constructor(props: InternalProps) {
    super(props);
    this.state = {
      allOptions: [],
      options: [],
    };
  }

  componentDidMount() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (this.filterNode && !(window as any).TOUCH_ENABLED) {
      this.filterNode.focus();
    }

    const allOptions = this.mapAllBusiness();

    this.setState({
      allOptions,
      options: allOptions,
    });

    this.setSearchResults();
  }

  mapAllBusiness() {
    const { businesses, activeBusiness } = this.props;

    const options: Option[] = [];
    businesses.forEach((business) => {
      const option = {
        business,
        name: deburr(business.name),
        key: safeToNumber(business.id),
      };

      if (business.id === activeBusiness?.id) {
        options.unshift(option);
      } else {
        options.push(option);
      }
    });

    return options;
  }

  searchBusinesses() {
    const { filter } = this.props;
    const { allOptions } = this.state;

    if (!isEmpty(filter)) {
      const filterOptions: Fuse.IFuseOptions<Option> = {
        keys: ['key', 'name'],
        threshold: 0.4,
      };
      const filterer = new Fuse(allOptions, filterOptions);

      return filterer.search(filter).map((result) => result.item) as Option[];
    }

    return allOptions;
  }

  handleFilter(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const { handleFilterChange } = this.props;

    handleFilterChange(value);

    this.setSearchResults();
  }

  setSearchResults = debounce(() => {
    const options = this.searchBusinesses();

    this.setState({
      options,
    });
  }, 200);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSetFilterNode(node: any) {
    this.filterNode = node;
  }

  render() {
    const {
      amountDisplayed,
      scrollableModal,
      filter,
      isEngOrHospitality: isEngOrHospitalityFromProps,
      isLoading: isLoadingFromProps,
      showOverlay: showOverlayFromProps,
      onSelectBusiness,
      pathname,
      selectedBusiness,
    } = this.props;

    const { options } = this.state;

    const isEngOrHospitality = isEngOrHospitalityFromProps || false;
    const isLoading = isLoadingFromProps || false;
    const showOverlay = showOverlayFromProps || false;

    const filterField = {
      value: filter,
      onBlur: noop,
      onChange: this.handleFilter,
    };
    const businessSwitcherClassName = clsx('BusinessSwitcher', {
      'BusinessSwitcher-loading': showOverlay && isLoading,
    });
    const businessSwitcherRestaurantListClassName = clsx('BusinessSwitcher-restaurantList', {
      'BusinessSwitcher-restaurantList--scrollableModal': scrollableModal,
    });

    // This is only used in dashboard and consumer

    return (
      <div className={businessSwitcherClassName}>
        <div className="BusinessSwitcher-filter">
          <TextInput
            autoFocus
            field={filterField}
            placeholder={this.props.placeholder}
            ref={this.handleSetFilterNode}
            onChange={this.props.onInputChange}
          />
        </div>
        <LoadingComponent
          isLoading={isLoading}
          spinnerClassName="BusinessSwitcher-spinner"
          showOverlay={showOverlay}
          loadingContainerClassName="BusinessSwitcher-loading"
        >
          <ol className={businessSwitcherRestaurantListClassName}>
            {options.slice(0, amountDisplayed).map((option) => (
              <BusinessSwitcherItem
                pathname={pathname}
                business={option.business}
                isEngOrHospitality={isEngOrHospitality}
                key={option.key}
                onClick={onSelectBusiness}
                selected={option.business === selectedBusiness}
              />
            ))}
          </ol>
        </LoadingComponent>
      </div>
    );
  }
}

export default BusinessSwitcher;
